import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAllEngagements, getEngagementById } from '../../store/actions';
import { Header } from '../../components/layout/Header';
import { Badge } from '../../components/ui/Badge';
import { ENGAGEMENT_STAGE, ENGAGEMENT_STATUS, formatDate, formatINR, label } from '../../constants/garmentCodes';

const TABS = ['Overview', 'Wallet', 'Payment Receipts', 'Requirements', 'Fabrics', 'Designs', 'Board', 'Orders', 'Documents', 'Audit History'];
const TAB_ROUTES = {
  Wallet: 'wallets',
  'Payment Receipts': 'receipts',
  Requirements: 'requirements',
  Fabrics: 'fabrics',
  Designs: 'designs',
  Board: 'board',
  Orders: 'orders',
  Documents: 'documents',
  'Audit History': 'audit',
};

const isLow = (eng) => eng?.lowBalanceThreshold != null && (eng.availableBalance ?? 0) < eng.lowBalanceThreshold;

export default function Engagements() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const brandId = params.get('brandId') || '';
  const [activeTab, setActiveTab] = useState('Overview');

  const engagements = useSelector((state) => state.engagement.engagements);
  const selected = useSelector((state) => state.engagement.engagement);
  const loading = useSelector((state) => state.engagement.loading);

  useEffect(() => {
    dispatch(
      getAllEngagements({
        brandId,
        status: '',
        callback: {
          success: (list) => {
            if (list.length) dispatch(getEngagementById({ id: list[0].id }));
          },
        },
      }),
    );
  }, [dispatch, brandId]);

  const goTo = (tab) => {
    if (!selected) return;
    navigate(`/dashboard/${TAB_ROUTES[tab]}?engagementId=${selected.id}`);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#f8f9fa]">
      <Header title="Engagements" breadcrumbs={[{ workspace: true }, { label: 'Engagements' }]} />
      <main className="flex-1 overflow-auto p-6 pb-20 md:pb-6">
        {!loading && engagements.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-100 py-12 text-center flex flex-col items-center justify-center">
            <p className="text-[14px] font-medium text-gray-800 mb-1">No engagements yet</p>
            <p className="text-[12px] text-gray-500 mb-4 max-w-sm">Engagements are automatically created when you onboard a new Brand.</p>
            <button onClick={() => navigate('/dashboard/brands')} className="px-4 py-2 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">
              Go to Brands
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {engagements.map((eng) => {
            const status = label(ENGAGEMENT_STATUS, eng.status);
            return (
              <button
                key={eng.id}
                onClick={() => dispatch(getEngagementById({ id: eng.id }))}
                className={`text-left p-4 rounded-lg border transition-all ${eng.__optimistic ? 'opacity-60' : ''} ${
                  String(selected?.id) === String(eng.id) ? 'border-[#1b3a6b] bg-[#1b3a6b]/5 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[13px] font-semibold text-gray-900">{eng.brandName || eng.name}</span>
                  <Badge status={status} />
                </div>
                <p className="text-[11px] text-gray-400 mb-3">{label(ENGAGEMENT_STAGE, eng.stage)}</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-[11px]">
                    <span className="text-gray-500">Available</span>
                    <span className={`font-mono font-semibold ${isLow(eng) ? 'text-amber-600' : 'text-green-600'}`}>{formatINR(eng.availableBalance)}</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-gray-500">Total balance</span>
                    <span className="font-mono text-gray-700">{formatINR(eng.walletBalance)}</span>
                  </div>
                </div>
                {isLow(eng) && <div className="mt-2 text-[10px] text-amber-600 font-medium">⚠ Low balance</div>}
              </button>
            );
          })}
        </div>

        {selected && (
          <div className="bg-white rounded-lg border border-gray-100">
            <div className="px-6 py-4 border-b border-gray-100 flex items-start gap-6 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-[15px] font-semibold text-gray-900">{selected.name}</h2>
                  <Badge status={label(ENGAGEMENT_STATUS, selected.status)} />
                </div>
                <div className="flex items-center gap-4 text-[12px] text-gray-500 flex-wrap">
                  <span>Started {formatDate(selected.startDate)}</span>
                  <span>·</span>
                  <span>Coordinator: {selected.coordinator || '—'}</span>
                  <span>·</span>
                  <span>Stage: <span className="font-medium text-gray-700">{label(ENGAGEMENT_STAGE, selected.stage)}</span></span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Available</p>
                  <p className={`text-[18px] font-bold font-mono ${isLow(selected) ? 'text-amber-600' : 'text-green-600'}`}>{formatINR(selected.availableBalance)}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => goTo('Payment Receipts')} className="px-3 py-1.5 text-[12px] text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Receipts</button>
                  <button onClick={() => goTo('Wallet')} className="px-3 py-1.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">+ Debit</button>
                </div>
              </div>
            </div>

            <div className="flex gap-0.5 overflow-x-auto px-6 border-b border-gray-100">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => (tab === 'Overview' ? setActiveTab(tab) : goTo(tab))}
                  className={`px-3 py-2.5 text-[12px] font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                    activeTab === tab ? 'border-[#1b3a6b] text-[#1b3a6b]' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="p-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Active Requirements</p>
                <p className="text-[24px] font-bold text-gray-900">{selected.activeRequirements ?? 0}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Active Orders</p>
                <p className="text-[24px] font-bold text-gray-900">{selected.activeOrders ?? 0}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Pending Approvals</p>
                <p className="text-[24px] font-bold text-amber-600">{selected.pendingApprovals ?? 0}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Held Amount</p>
                <p className="text-[24px] font-bold font-mono text-gray-900">{formatINR(selected.heldAmount)}</p>
              </div>
            </div>
          </div>
        )}
      </main>


    </div>
  );
}
